import { useState, useCallback } from 'react';
import { apiPatch, ApiResponse, ApiError } from '../../services/api.service';
import type { Extra } from '../../types';

interface UseUpdateExtraReturn {
  updateExtra: (
    extraId: number | string,
    data: Partial<Extra>,
    disableNotifications?: boolean
  ) => Promise<Extra | null>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Custom hook to update extra/profile data
 * Notifications are handled automatically by the API service for mutations
 * @returns Function to update extra, loading state, error state, and success state
 */
export function useUpdateExtra(): UseUpdateExtraReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const updateExtra = useCallback(
    async (
      extraId: number | string,
      data: Partial<Extra>,
      disableNotifications: boolean = false
    ): Promise<Extra | null> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        // Remove fields that shouldn't be sent in updates
        const { extra_id, created_at, last_login_at, ...updateData } = data;

        const response: ApiResponse<Extra> = await apiPatch<Extra>(
          `/extras/${extraId}`,
          updateData,
          {
            notification: {
              enabled: !disableNotifications,
              successMessage: 'Profil erfolgreich aktualisiert!',
              errorMessage: 'Fehler beim Aktualisieren des Profils. Bitte versuchen Sie es erneut.',
            },
          }
        );

        if (response.success && response.data) {
          setSuccess(true);
          return response.data;
        } else {
          setError(response.error || 'Fehler beim Aktualisieren des Extras');
          return null;
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Ein unerwarteter Fehler ist aufgetreten');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    updateExtra,
    loading,
    error,
    success,
  };
}
